import React, { useCallback } from 'react';
import { TreeItem } from '@material-ui/lab';
import classNames from 'classnames';
import './TreeNode.less';
import { SkeletonTree } from 'loaders/SkeletonTree';
import { NodeLoadingType } from './Tree';

export enum NodePosition {
  first,
  middle,
  last,
}

function getTreeNodeClassNames(
  expanded?: boolean,
  isLeaf?: boolean,
  baseClassName?: string,
  additionalClassName?: string,
  position?: NodePosition
) {
  return classNames(
    `dpc-tree-node`,
    { 'dpc-tree-node--expanded': expanded },
    { 'dpc-tree-node--leaf': isLeaf },
    { 'dpc-tree-node--first': position === NodePosition.first },
    { 'dpc-tree-node--last': position === NodePosition.last },
    baseClassName,
    additionalClassName
  );
}

/**
 * @deprecated Please use /CommonTree/Tree.tsx
 */
export interface ITreeNode<T = undefined> {
  classNames?: string;
  isExpanded: boolean;
  isLoading?: boolean;
  isActive?: boolean;
  children?: ITreeNode<T>[];
  nodeId: string;
  hasChildren?: boolean;
  data: T;
  onNodeKeyDown?: (node: ITreeNode<T>, e: React.KeyboardEvent) => void;
  onNodeKeyUp?: (node: ITreeNode<T>, e: React.KeyboardEvent) => void;
  onNodeExpand?: (node: ITreeNode<T>) => void;
  onNodeFold?: (node: ITreeNode<T>) => void;
  onNodeSelect?: (node: ITreeNode<T>) => void;
  /*
   * When true, children for this node will always load.
   */
  alwaysLoadChildren?: boolean;
}

export function getNodePosition(index: number, arrayLength: number): NodePosition {
  if (index === 0) {
    return NodePosition.first;
  } else if (index === arrayLength - 1) {
    return NodePosition.last;
  } else {
    return NodePosition.middle;
  }
}

export interface ITreeNodeProps<T> extends ITreeNode<T> {
  baseClassName?: string;
  nodeLoadingType?: NodeLoadingType
  getTreeNode?: (node: ITreeNode<T>) => React.ReactNode;
  nodePosition?: NodePosition;
}

/**
 * @deprecated Please use /CommonTree/Tree.tsx
 */
export function TreeNode<T>(props: ITreeNodeProps<T>) {
  const {
    isExpanded,
    classNames,
    children,
    getTreeNode,
    baseClassName,
    hasChildren,
    nodePosition,
    onNodeKeyDown,
    onNodeSelect,
    onNodeExpand,
    onNodeFold,
    nodeLoadingType = NodeLoadingType.Spinner
  } = props;

  const getNodeProps = useCallback(
    (node: ITreeNode<T>) => {
      return {
        ...node,
        getTreeNode: getTreeNode
          ? (node: ITreeNode<T>) => {
              return getTreeNode(node);
            }
          : undefined,
        baseClassName: baseClassName,
        onNodeKeyDown,
        onNodeFold,
        onNodeExpand,
        onNodeSelect,
      };
    },
    [baseClassName, getTreeNode, onNodeExpand, onNodeFold, onNodeKeyDown, onNodeSelect]
  );

  const onNodeKeyDownEvent = useCallback(
    (e: React.KeyboardEvent) => {
      onNodeKeyDown?.(props, e);

      if (e.key === 'Enter') onNodeSelect?.(props);
      if (e.key === 'ArrowRight' && !props.isExpanded) onNodeExpand?.(props);
      if (e.key === 'ArrowLeft' && props.isExpanded) onNodeFold?.(props);

      e.stopPropagation();
    },
    [onNodeKeyDown, props, onNodeSelect, onNodeExpand, onNodeFold]
  );

  return (
    <TreeItem
      classes={{
        group: 'dpc-tree-node__children',
        content: 'dpc-tree-node__item',
        iconContainer: 'dpc-tree-node__icon-container',
      }}
      nodeId={props.nodeId}
      label={getTreeNode && getTreeNode(props)}
      className={getTreeNodeClassNames(isExpanded, !hasChildren, baseClassName, classNames, nodePosition)}
      onKeyDown={onNodeKeyDownEvent}
    >
      {(props.isLoading && nodeLoadingType === NodeLoadingType.Skeleton) ? (
        <SkeletonTree model={new Map([
          ["1", 0],
          ["2", 0]
        ])} />
      ) : (
        children?.map((node: ITreeNode<T>, i, arr) => {
          return <TreeNode key={node.nodeId} nodeLoadingType={nodeLoadingType} nodePosition={getNodePosition(i, arr.length)} {...getNodeProps(node)} />;
        })
      )}
    </TreeItem>
  );
}
